import { getSessionToken } from '@/api/sessionToken'

/** Transport-independent app authentication; business hooks own reconnect/backoff. */
export function createSocketAuth(ws: WebSocket, onReady: () => void) {
  let disposed = false
  let ready = false
  let pending = false
  let timeout: ReturnType<typeof setTimeout> | undefined
  const dispose = () => {
    disposed = true
    clearTimeout(timeout)
    window.removeEventListener('auth:unauthorized', logout)
    window.removeEventListener('offline', offline)
  }
  const logout = () => { dispose(); ws.close(4001, 'Session ended') }
  const offline = () => { dispose(); ws.close(4000, 'Network offline') }
  const sendToken = async (type: 'auth' | 'auth.refresh') => {
    if (disposed || pending) return
    if (!navigator.onLine) { offline(); return }
    pending = true
    clearTimeout(timeout)
    timeout = setTimeout(() => { dispose(); ws.close(4001, 'Authentication timeout') }, 10_000)
    try {
      const token = await getSessionToken()
      if (disposed || ws.readyState !== WebSocket.OPEN) return
      if (!token) { logout(); return }
      ws.send(JSON.stringify({ type, token }))
    } catch {
      // Network failures may recover through the hook's backoff. Never log tokens.
      if (!disposed) { dispose(); ws.close(4001, 'Authentication unavailable') }
    } finally { pending = false }
  }
  window.addEventListener('auth:unauthorized', logout)
  window.addEventListener('offline', offline)
  return {
    open: () => sendToken('auth'),
    handle(frame: { type?: string }): boolean {
      if (frame.type === 'auth.required') { void sendToken('auth.refresh'); return true }
      if (frame.type === 'auth.ready') {
        clearTimeout(timeout)
        if (!disposed && !ready) { ready = true; onReady() }
        return true
      }
      return false
    },
    dispose,
  }
}
